<template>
  <div class="st-table">
      <el-card shadow="always" style="width: 100%">
        <el-form class="report-form" ref="form" :model="formItem" label-position="left" label-width="145px" label-colon>
          <el-row :gutter="16">
            <el-col :span="3">
                <el-form-item label="Week">
                  <el-date-picker
                    v-model="week"
                    placeholder="Please Input"
                    clearable
                    type="week"
                    style="width:260px"
                    :format="start+ ' to '+ end"
                    @change="newDateWeekHandle"
                  >
                  </el-date-picker>
                </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="16">
            <el-col :span="3">
              <el-form-item label="Class">
                  <el-cascader
                    v-model= "cateVal"
                    :options = "cateList"
                    :props="{ checkStrictly: true }"
                    placeholder="Please Input"
                    clearable
                    filterable
                    :filter-method="dataFilter"
                    @change="handleChange"
                    style="width:260px"
                    >
                  </el-cascader>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="16">
            <el-col :span="3">
              <el-form-item label="Sub Class">
                <el-select v-model="formItem.subClass"
                           clearable
                           placeholder="Please Input"
                           style="width:260px"
                >
                  <el-option
                    v-for="item in this.subClass"
                    :key="item"
                    :label="item.label"
                    :value="item">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="16">
            <el-col :span="3">
              <el-form-item label="Description">
                  <el-input
                    type="textarea"
                    v-model="formItem.description"
                    placeholder="Please Input"
                    :rows="2"
                    style="width:720px"
                  >
                  </el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="16" style="margin-top: 10px">
            <el-col :span="20">
              <el-form-item
                label="Project Progress"
                class="project-progress-form-item"
                :class="{ 'project-progress-form-item--disabled': !projectProgressEnabled }"
              >
                <el-slider
                  v-model="percentage"
                  :disabled="!projectProgressEnabled"
                  show-input
                  input-size="medium"
                  :step="5"
                  class="project-progress-slider"
                />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="16">
            <el-col :span="20">
              <el-form-item label="Time">
                <div class="time-field-row">
                  <div class="time-input-wrap">
                    <el-input-number
                      v-model="formItem.time"
                      :precision="1"
                      :step="0.5"
                      :min="0.5"
                      :max="WEEK_CAP"
                      style="width:160px"
                    ></el-input-number>
                    <div class="hours-under">Hours</div>
                  </div>
                  <div v-if="start && end" class="time-hint-wrap">
                    <div
                      v-if="weekTimeExhausted"
                      class="week-hours-hint week-hours-warn"
                    >
                      <div class="week-hint-line">{{ WEEK_CAP }}h weekly cap is reached for submitted entries.</div>
                      <div class="week-hint-line">New submit will be rejected if total exceeds {{ WEEK_CAP }}h.</div>
                    </div>
                    <div v-else class="week-hours-hint">
                      Logged this week: <strong>{{ weekLoggedHours.toFixed(1) }}</strong> h;
                      remaining after this line:
                      <strong>{{ remainingAfterThisLine.toFixed(1) }}</strong> h
                      <span class="week-cap">(weekly cap {{ WEEK_CAP }} h)</span>
                    </div>
                  </div>
                </div>
              </el-form-item>
            </el-col>
          </el-row>
          <div style="margin-top: 5px; text-align: left">
            <el-button type="primary" @click="submitReport">
              Submit
            </el-button>
          </div>
        </el-form>
        <div v-if="start && end" class="week-submitted-block">
          <div class="week-submitted-title">Submitted this week</div>
          <el-table
            v-loading="weekListLoading"
            class="week-submitted-table"
            :data="weekReportList"
            :row-key="weekReportRowKey"
            :max-height="weekSubmittedTableMaxHeight"
            border
            stripe
            size="small"
            style="width: 100%"
            empty-text="No entries for this week yet. They appear here after you submit."
          >
            <el-table-column prop="category" label="Class" min-width="100" show-overflow-tooltip />
            <el-table-column prop="category2nd" label="Class Lv2" min-width="110" show-overflow-tooltip />
            <el-table-column prop="category3rd" label="Class Lv3" min-width="110" show-overflow-tooltip />
            <el-table-column label="Sub Class" min-width="100" show-overflow-tooltip>
              <template slot-scope="scope">
                {{ formatSubClass(scope.row) }}
              </template>
            </el-table-column>
            <el-table-column prop="description" label="Description" min-width="200" show-overflow-tooltip>
              <template slot-scope="scope">
                <input
                  v-if="isWeekRowEditing(scope.row)"
                  v-model="weekPatchItem.description"
                  type="text"
                  class="week-table-cell-input"
                />
                <span v-else>{{ scope.row.description }}</span>
              </template>
            </el-table-column>
            <el-table-column label="Hours" width="100" align="right">
              <template slot-scope="scope">
                <input
                  v-if="isWeekRowEditing(scope.row)"
                  v-model="weekPatchItem.time"
                  type="text"
                  class="week-table-cell-input week-table-cell-input-hours"
                />
                <span v-else>{{ scope.row.time }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="percentage" label="Progress %" width="100" align="center">
              <template slot-scope="scope">
                {{ scope.row.percentage != null && scope.row.percentage !== '' ? scope.row.percentage : '—' }}
              </template>
            </el-table-column>
            <el-table-column label="Action" width="168" align="center">
              <template slot-scope="scope">
                <div v-if="isWeekRowEditing(scope.row)" class="week-table-actions">
                  <el-button size="mini" type="primary" plain @click="handleWeekRowSave(scope.row)">
                    Save
                  </el-button>
                  <el-button size="mini" plain @click="handleWeekRowCancel">
                    Cancel
                  </el-button>
                </div>
                <div v-else class="week-table-actions">
                  <el-button type="primary" size="mini" @click="handleWeekRowEdit(scope.row)">
                    Edit
                  </el-button>
                  <el-button
                    size="mini"
                    type="danger"
                    class="week-table-btn-delete"
                    @click="handleWeekRowDelete(scope.row)"
                  >
                    Delete
                  </el-button>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-card>
  </div>
</template>

<script>

import reportApi from '@/api/report.js';
import projectApi from "@/api/project.js";

export default {
  name: "weeklyReport",

  data() {
    return {
      loading: false,
      start: "",
      end: "",
      week:"",
      projectList:[],
      cateList: [],
      cateVal: [],
      percentage: 0,
      customColors: [
        {color: '#f56c6c', percentage: 20},
        {color: '#e6a23c', percentage: 40},
        {color: '#5cb87a', percentage: 60},
        {color: '#1989fa', percentage: 80},
        {color: '#6f7ad3', percentage: 100}
      ],
      formItem: {
        // "project_id":'',
        "description":'',
        "time":'',
        "subClass": ''
      },
      /** Sum of hours already submitted for the selected week (excludes current form line) */
      weekLoggedHours: 0,
      WEEK_CAP: 40,
      /** 用于「超过 cap 行时」限制表体高度；略大于真实行高，避免 9 行仍被误判为能塞进 cap 行 */
      weekSubmittedRowHeight: 52,
      weekSubmittedHeaderHeight: 48,
      weekSubmittedVisibleRowCap: 8,
      weekReportList: [],
      weekListLoading: false,
      /** Which report row is being edited (primary key); null if none */
      weekTableEditingId: null,
      weekPatchItem: {
        time: '',
        description: '',
        percentage: '',
      },
    }
  },
  computed: {
    remainingWeekBudget() {
      return Math.max(0, this.WEEK_CAP - this.weekLoggedHours)
    },
    weekTimeExhausted() {
      return this.remainingWeekBudget < 0.5
    },
    currentLineHours() {
      const t = Number(this.formItem.time)
      return isNaN(t) ? 0 : t
    },
    remainingAfterThisLine() {
      return Math.max(
        0,
        this.WEEK_CAP - this.weekLoggedHours - this.currentLineHours
      )
    },
    projectProgressEnabled() {
      return Array.isArray(this.cateVal) && this.cateVal[0] === 'Projects'
    },
    /**
     * 仅当 n > cap 时设置 max-height，表体在 el-table 内滚动；
     * n <= cap 时不限制高度（由表格自然撑开），避免错误地用「cap 行」去算总高导致永远不出内滚动。
     */
    weekSubmittedTableMaxHeight() {
      const n = Array.isArray(this.weekReportList) ? this.weekReportList.length : 0
      const header = this.weekSubmittedHeaderHeight
      const row = this.weekSubmittedRowHeight
      const cap = this.weekSubmittedVisibleRowCap
      if (n === 0) {
        return header + Math.min(4, cap) * row
      }
      if (n <= cap) {
        return undefined
      }
      return header + cap * row
    },
  },
  mounted() {
    this.getProjectList()
    this.getCategoryList()
  },
  methods: {
    customColorMethod() {
      if (this.percentage < 30) {
        return '#909399';
      } else if (this.percentage < 70) {
        return '#e6a23c';
      } else {
        return '#67c23a';
      }
    },
    increase() {
      this.percentage += 10;
      if (this.percentage > 100) {
        this.percentage = 100;
      }
    },
    decrease() {
      this.percentage -= 10;
      if (this.percentage < 0) {
        this.percentage = 0;
      }
    },
    newDateWeekHandle() {
      const now = new Date(this.week);
      const nowTime = now.getTime();
      const day = now.getDay();
      const oneDayTime = 24*60*60*1000;

      //const mondayTime = nowTime - (day-1)*oneDayTime;
      const sundayTime = nowTime + (day-2)*oneDayTime;
      const saturdayTime = nowTime + (7-day-1)*oneDayTime;
      this.start = this.$moment(sundayTime).format('YYYY-MM-DD');
      this.end = this.$moment(saturdayTime).format('YYYY-MM-DD');
      this.weekTableEditingId = null
      this.fetchWeekLoggedHours();
    },
    fetchWeekLoggedHours() {
      if (!this.start || !this.end) {
        this.weekLoggedHours = 0
        this.weekReportList = []
        return
      }
      const para = { week_start: this.start, week_end: this.end }
      this.weekListLoading = true
      reportApi.getReport(para).then(res => {
        const rows =
          res && res.data && res.data.data ? res.data.data : []
        this.weekReportList = Array.isArray(rows) ? rows.slice() : []
        let sum = 0
        for (let i = 0; i < this.weekReportList.length; i++) {
          sum += Number(this.weekReportList[i].time) || 0
        }
        this.weekLoggedHours = sum
      }).catch(() => {
        this.weekLoggedHours = 0
        this.weekReportList = []
      }).finally(() => {
        this.weekListLoading = false
      })
    },
    weekReportRowKey(row) {
      return row.id
    },
    isWeekRowEditing(row) {
      return this.weekTableEditingId != null && row.id === this.weekTableEditingId
    },
    formatSubClass(row) {
      const v = row.subClass != null ? row.subClass : row.sub_class
      if (v == null) return '—'
      if (typeof v === 'object' && v !== null) {
        return v.label != null ? v.label : JSON.stringify(v)
      }
      return String(v)
    },
    handleWeekRowEdit(row) {
      const t = row.time != null && row.time !== '' ? String(row.time) : ''
      this.weekPatchItem.time = t
      this.weekPatchItem.description =
        row.description != null && row.description !== ''
          ? String(row.description)
          : ''
      this.weekPatchItem.percentage = row.percentage
      this.weekTableEditingId = row.id
    },
    handleWeekRowCancel() {
      this.weekTableEditingId = null
    },
    handleWeekRowDelete(row) {
      this.$confirm('Delete this report entry?', 'Confirm', {
        type: 'warning',
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
      })
        .then(() => {
          const para = { id: row.id }
          return reportApi.deleteReport(para)
        })
        .then(resp => {
          this.$message.success({
            message: (resp && resp.msg) || 'Deleted',
            type: 'success',
          })
          if (this.weekTableEditingId === row.id) {
            this.weekTableEditingId = null
          }
          this.fetchWeekLoggedHours()
        })
        .catch(() => {})
    },
    handleWeekRowSave(row) {
      const newTime = parseFloat(String(this.weekPatchItem.time).trim())
      if (isNaN(newTime) || newTime < 0.5) {
        this.$message.warning('Please enter a valid time (minimum 0.5 h).')
        return
      }
      const others = this.weekReportList.reduce((sum, r) => {
        if (r.id === row.id) return sum
        return sum + (Number(r.time) || 0)
      }, 0)
      if (others + newTime > this.WEEK_CAP) {
        this.$message.warning(
          `Total weekly hours cannot exceed ${this.WEEK_CAP} h after this change.`
        )
        return
      }
      const para = { id: row.id }
      const data = {
        time: newTime,
        description: this.weekPatchItem.description,
        percentage: this.weekPatchItem.percentage,
      }
      reportApi.patchReport(para, data).then(resp => {
        this.$message.success({
          message: (resp && resp.msg) || 'Updated',
          type: 'success',
        })
        this.weekTableEditingId = null
        this.fetchWeekLoggedHours()
      })
    },
    getDateWeek() {
      const now = new Date();
      const nowTime = now.getTime();
      const day = now.getDay();
      const oneDayTime = 24*60*60*1000;
      const mondayTime = nowTime - (day-1)*oneDayTime
      this.week = new Date(mondayTime);
      this.newDateWeekHandle();
    },
    handleChange() {
      if (!this.projectProgressEnabled) {
        this.percentage = 0
      }
      console.log(this.cateVal);
    },
    getProjectList() {
      projectApi.getProjects().then(
        res => {
          this.projectList = res.data.data
        }
      )
    },
    getCategoryList() {
      reportApi.getCategory().then(
        res => {
          this.cateList = res.data.data
          this.subClass = res.data.subClass
        }
      )
    },
    submitReport() {
      const t = Number(this.formItem.time)
      if (isNaN(t) || t < 0.5) {
        this.$message.warning('Please enter a valid time (minimum 0.5 h).')
        return
      }
      if (this.weekLoggedHours + t > this.WEEK_CAP) {
        this.$message.warning(
          `Cannot submit: total would exceed the weekly cap of ${this.WEEK_CAP} h (already logged ${this.weekLoggedHours.toFixed(1)} h).`
        )
        return
      }
      let payload = this.formItem
      payload['week_start'] = this.start
      payload['week_end'] = this.end
      // payload['project_id'] = this.formItem.project_id
      payload['description'] = this.formItem.description
      payload['percentage'] = this.projectProgressEnabled ? this.percentage : null
      payload['time'] = this.formItem.time
      payload['category'] = this.cateVal[0]
      payload['category2nd'] = this.cateVal[1] ? this.cateVal[1] : null
      payload['category3rd'] = this.cateVal[2] ? this.cateVal[2] : null
      payload['subClass'] = this.formItem.subClass
      reportApi.createReport(payload).then(() => {
        this.fetchWeekLoggedHours()
        this.handleReset()
      })
    },
    handleReset() {
      this.cateVal = []
      this.formItem.description = null
      this.formItem.subClass = null
      this.formItem.time = null
      this.percentage = 0
    },
    dataFilter(node, val) {
      if (!!~node.text.indexOf(val) || !!~node.text.toUpperCase().indexOf(val.toUpperCase())) {
        return true
      }
    },
  },
  created() {
    this.getDateWeek()
  }

};
</script>



<style lang="scss" scoped>
.st-table {
  padding:10px
}

.project-progress-form-item ::v-deep .el-form-item__content {
  line-height: normal;
}

/* 与 el-slider disabled 同步：标签一并呈禁用灰态 */
.project-progress-form-item--disabled ::v-deep .el-form-item__label {
  color: #c0c4cc;
  cursor: not-allowed;
}

.project-progress-slider {
  width: 720px;
  max-width: 100%;
}

.time-field-row {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex-wrap: wrap;
  width: 100%;
  max-width: 960px;
}

.time-input-wrap {
  flex: 0 0 auto;
}

.hours-under {
  margin-top: 6px;
  font-size: 12px;
  color: #606266;
}

.time-hint-wrap {
  flex: 1;
  margin-left: 28px;
  min-width: 220px;
  max-width: 520px;
  padding-top: 2px;
}

.week-hours-hint {
  margin-top: 0;
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
}

.week-cap {
  color: #909399;
}

.week-hours-warn {
  color: #e6a23c;
}

.week-hint-line + .week-hint-line {
  margin-top: 6px;
}

.week-submitted-block {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #ebeef5;
}

.week-submitted-title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}

.week-submitted-table ::v-deep .el-table__header-wrapper th {
  padding: 6px 0;
}

.week-submitted-table ::v-deep .el-table__body-wrapper td {
  padding: 4px 0;
  vertical-align: middle;
}

.week-submitted-table ::v-deep .cell {
  line-height: 1.35;
  padding-left: 8px;
  padding-right: 8px;
}

.week-table-actions {
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  gap: 6px;
  vertical-align: middle;
}

.week-table-actions ::v-deep .el-button--mini {
  margin: 0;
  padding: 5px 10px;
}

.week-table-btn-delete {
  margin-left: 0;
}

.week-table-cell-input {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 2px 6px;
  border: 1px solid #dcdfe6;
  border-radius: 3px;
  font-size: 12px;
  line-height: 1.3;
}

.week-table-cell-input-hours {
  text-align: right;
}

</style>

